import type { ReactNode } from 'react';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner/LoadingSpinner';
import styles from './SearchDropdown.module.scss';

export interface SearchDropdownProps {
  /**
   * Whether the dropdown is visible. Drive this from the *raw* input value, not
   * the debounced one, so the panel opens on the keystroke that crosses the
   * search threshold instead of waiting out the debounce.
   */
  isOpen: boolean;
  /** A search is in flight (including the debounce window before it starts). */
  isLoading: boolean;
  isError: boolean;
  /** The search settled and came back with zero results. */
  isEmpty: boolean;
  loadingLabel: string;
  errorLabel: string;
  emptyLabel: string;
  /** Result rows, rendered only once the search settled with matches. */
  children?: ReactNode;
}

/**
 * Result panel for the wizard's async search inputs.
 *
 * Owns the loading / error / no-results states so an in-flight or empty search
 * is never indistinguishable from a broken input. Callers keep ownership of the
 * result rows themselves — each search has its own row markup and select
 * handler.
 */
export function SearchDropdown({
  isOpen,
  isLoading,
  isError,
  isEmpty,
  loadingLabel,
  errorLabel,
  emptyLabel,
  children,
}: SearchDropdownProps) {
  if (!isOpen) return null;

  return (
    <div className={styles.dropdown}>
      {isLoading ? (
        <div className={styles.state}>
          <LoadingSpinner size="sm" label={loadingLabel} />
          <span>{loadingLabel}</span>
        </div>
      ) : isError ? (
        <div className={styles.state} role="alert">
          <span>{errorLabel}</span>
        </div>
      ) : isEmpty ? (
        <div className={styles.state}>
          <span>{emptyLabel}</span>
        </div>
      ) : (
        children
      )}
    </div>
  );
}
